import { useState, useCallback, useEffect, useRef } from "react";
import { ChatState, ChatMessage, SidebarSection } from "../types/chatTypes";
import type { ChatStep } from "../types/stream.types";
import { 
  createConversation, 
  addMessage, 
  getConversationMessages,
  getUserConversations,
  updateMessageFeedback,
  deleteMessage,
  deleteConversation,
  updateChangesApplicationStatus,
  runMultiagentChatStream,
  loadRedlineDocumentIfPresent
} from "../services/conversationService";
import { configService } from "../../../shared/services/configService";
import type { Source } from "../../../types";
import { TemplateService } from "../../../shared/services/templateService";

// Types for better type safety
interface ConversationWithId {
  id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  workpill_execution_id?: string | null;
  _workpill_id?: string; // Session-only property
}

interface MessageWithSources extends ChatMessage {
  id: string; // Asegurar que id existe
  content: string; // Asegurar que content existe
  sources?: Source[];
  changes?: string; // OOXML string with track changes
  completedThoughts?: {
    agent: string;
    thought: string;
    completion_order: number;
    toolResult?: any; // 🆕 Preservar toolResult completo para renderizado específico
  }[];
  // 🔄 Estado de aplicación de changes
  changesStatus?: 'applied' | 'cancelled' | 'accepted' | 'rejected' | null;
  changesUpdatedAt?: Date | null;
  streamedFinalText?: string;
  finalResponseComplete?: boolean;
  finalResponseTraceId?: string | null;
  finalChunkCount?: number;
  lastChunkSequence?: number | null;
  agentTimelineSteps?: ChatStep[];
}



// 🔄 CONVERTIR RAW STEPS A FORMATO UI
const convertRawStepsToThoughts = (rawSteps: any[]): any[] => {
  return rawSteps.map(step => {
    if (step.type === "thought") {
      return {
        type: "agent_update",
        agent: step.agent,
        thought: step.message || "",
        completion_order: step.order,
        is_final: step.metadata?.is_final || false,
        timestamp: step.metadata?.timestamp
      };
    } else if (step.type === "tool_call") {
      return {
        type: "tool_result",
        agent: step.agent, // En raw format, agent es el tool_name
        thought: step.output?.content || "",
        completion_order: step.order,
        tool_name: step.metadata?.tool_name || step.agent,
        tool_call_id: step.metadata?.tool_call_id,
        success: step.output?.success,
        content: step.output?.content,
        toolResult: {
          agent: step.agent,
          tool_name: step.metadata?.tool_name || step.agent,
          content: step.output?.content,
          success: step.output?.success,
          tool_call_id: step.metadata?.tool_call_id,
          metadata: step.output?.metadata || {},
          timestamp: step.metadata?.timestamp
        },
        timestamp: step.metadata?.timestamp
      };
    }
    return null;
  }).filter(Boolean);
};

function normalizeChatStepsFromDb(raw: any[]): ChatStep[] {
  const out: ChatStep[] = [];
  for (let i = 0; i < raw.length; i++) {
    const s = raw[i];
    const t = s?.type;
    if (t !== 'thought' && t !== 'tool_call' && t !== 'text_output') continue;
    out.push({
      order: typeof s.order === 'number' ? s.order : i,
      agent: s.agent || 'assistant',
      type: t,
      message: s.message,
      input: s.input,
      output: s.output,
      metadata: s.metadata || {},
    });
  }
  return out;
}
// PROCESADOR CENTRALIZADO DE MENSAJES
const processMessage = (msg: any): MessageWithSources => {
  let completedThoughts: any[] = [];
  let agentTimelineSteps: ChatStep[] | undefined = undefined;

  const stepsContainer = (msg as any).steps ?? (msg as any).metadata?.steps;
  const stepsArray = stepsContainer?.steps;

  if (Array.isArray(stepsArray) && stepsArray.length > 0) {
    const firstStep = stepsArray[0];
    const isNewTimelineFormat =
      firstStep &&
      (firstStep.type === 'thought' ||
        firstStep.type === 'tool_call' ||
        firstStep.type === 'text_output');

    if (isNewTimelineFormat) {
      agentTimelineSteps = normalizeChatStepsFromDb(stepsArray);
      completedThoughts = convertRawStepsToThoughts(
        stepsArray.filter((s: any) => s.type !== 'text_output')
      );
      console.log('[processMessage] Timeline steps from DB:', {
        messageId: msg.id,
        rawStepsCount: stepsArray.length,
        timelineCount: agentTimelineSteps.length,
        convertedThoughtsCount: completedThoughts.length,
      });
    } else {
      completedThoughts = stepsArray.map((step: any) => {
        if (step.agent || step.thought || step.tool_name) {
          return {
            ...step,
            agent: step.agent || step.agent_name || 'WorkOps Agent',
            thought: step.thought || step.response || step.content || '',
          };
        }
        return {
          agent: step.agent_name || 'WorkOps Agent',
          thought: step.response || '',
          completion_order: step.step_id || 0,
        };
      });
    }
  }

  // PRIORIDAD 2: Metadata (solo para mensajes antiguos)
  else if ((msg as any).metadata?.agent_thoughts && Array.isArray((msg as any).metadata.agent_thoughts)) {
    completedThoughts = (msg as any).metadata.agent_thoughts;
    console.log('🔄 [processMessage] Using metadata.agent_thoughts (legacy):', {
      messageId: msg.id,
      thoughtsCount: completedThoughts.length
    });
  }
  // PRIORIDAD 3: Embedded comments (legacy)
  else if (msg.content && msg.content.includes('<!-- AGENT_THOUGHTS:')) {
    try {
      const match = msg.content.match(/<!-- AGENT_THOUGHTS:(.+?) -->/);
      if (match) {
        completedThoughts = JSON.parse(match[1]);
      }
    } catch (e) {
      console.warn('Error parsing embedded thoughts:', e);
    }
  }

  // 2. Limpiar content
  let cleanContent = msg.content || '';
  if (cleanContent.includes('<!-- AGENT_THOUGHTS:')) {
    cleanContent = cleanContent.replace(/\n\n<!-- AGENT_THOUGHTS:.+? -->/g, '').trim();
  }

  // 3. Extraer sources y changes
  const sources = (msg as any).metadata?.sources || (msg as any).sources || [];
  const changes = (msg as any).metadata?.changes || (msg as any).changes || '';
  const traceId = (msg as any).metadata?.trace_id || null;
  
  // 🔄 NUEVO: Extraer estado de aplicación de changes desde columnas dedicadas
  const changesStatus = (msg as any).changes_status || null;
  const changesUpdatedAtStr = (msg as any).changes_updated_at || null;
  const changesUpdatedAt = changesUpdatedAtStr ? new Date(changesUpdatedAtStr) : null;
  
  // 🔍 DEBUG: Detailed log when there are changes
  if ((msg as any).metadata?.changes || (msg as any).changes) {
    console.log('🔍 [processMessage] Message with changes processed:', {
      messageId: msg.id,
      hasChangesInMetadata: !!(msg as any).metadata?.changes,
      hasChangesField: !!(msg as any).changes,
      changesStatus,
      changesUpdatedAt,
      rawChangesStatus: (msg as any).changes_status,
      rawChangesUpdatedAt: (msg as any).changes_updated_at,
      fullMessage: {
        id: msg.id,
        changes_status: (msg as any).changes_status,
        changes_updated_at: (msg as any).changes_updated_at,
        metadata: (msg as any).metadata
      }
    });
  }

  return {
    id: msg.id,
    content: cleanContent,
    isUser: msg.is_user,
    timestamp: new Date(msg.sent_at || msg.created_at),
    feedbackGiven: msg.feedback ? (msg.feedback ? 'like' : 'dislike') : null,
    sources: sources,
    changes: changes,
    completedThoughts: completedThoughts,
    agentTimelineSteps,
    changesStatus: changesStatus,
    changesUpdatedAt: changesUpdatedAt ? new Date(changesUpdatedAt) : null,
    streamedFinalText: cleanContent,
    finalResponseComplete: true,
    finalResponseTraceId: traceId,
    finalChunkCount: 0,
    lastChunkSequence: null
  };
};

// 🔧 HELPER: Callbacks de streaming comunes (evita duplicación)
const createStreamingCallbacks = (
  setChatState: React.Dispatch<React.SetStateAction<ChatState>>,
  setCurrentStreamingThoughts: React.Dispatch<React.SetStateAction<{ agent: string; thought: string }[]>>
) => {
  // Buffer chunks and paint them at frame cadence for smoother typing.
  let pendingChunkBuffer = '';
  let chunkFlushRafId: number | null = null;
  let hasReceivedFinalChunk = false;
  let latestChunkMeta: {
    trace_id?: string;
    finalChunkCount: number;
    lastChunkSequence: number | null;
  } = {
    trace_id: undefined,
    finalChunkCount: 0,
    lastChunkSequence: null
  };

  let timelineSeq = 0;

  const appendProseToAgentTimeline = (steps: ChatStep[] | undefined, chunk: string): ChatStep[] => {
    const copy = [...(steps || [])];
    const last = copy[copy.length - 1];
    if (last?.type === 'text_output') {
      copy[copy.length - 1] = {
        ...last,
        message: (last.message || '') + chunk,
      };
    } else {
      copy.push({
        order: timelineSeq++,
        agent: 'assistant',
        type: 'text_output',
        message: chunk,
      });
    }
    return copy;
  };

  const pushThoughtTimeline = (
    steps: ChatStep[] | undefined,
    agentName: string,
    thought: string,
    isFinal: boolean
  ): ChatStep[] => [
    ...(steps || []),
    {
      order: timelineSeq++,
      agent: agentName,
      type: 'thought',
      message: thought,
      metadata: { is_final: isFinal },
    },
  ];

  const pushToolTimeline = (steps: ChatStep[] | undefined, tr: any): ChatStep[] => {
    const toolName = tr.tool_name || tr.metadata?.tool_name || 'tool';
    return [
      ...(steps || []),
      {
        order: timelineSeq++,
        agent: toolName,
        type: 'tool_call',
        output: {
          success: tr.success !== false,
          content: tr.content,
          metadata: tr.metadata || {},
        },
        metadata: {
          tool_name: toolName,
          tool_call_id: tr.tool_call_id,
          timestamp: tr.timestamp,
        },
      },
    ];
  };

  const flushChunkBuffer = () => {
    chunkFlushRafId = null;
    if (!pendingChunkBuffer.length) return;

    // Adaptive slice: small smooth batches for a fluid "typing" feel.
    const chunkSliceLength = Math.min(Math.max(18, Math.ceil(pendingChunkBuffer.length * 0.35)), 72);
    const nextSlice = pendingChunkBuffer.slice(0, chunkSliceLength);
    pendingChunkBuffer = pendingChunkBuffer.slice(chunkSliceLength);

    setChatState(prevState => ({
      ...prevState,
      messages: prevState.messages.map(msg => {
        if (msg.id === 'agent-streaming') {
          const currentMessage = msg as MessageWithSources;
          const updatedContent = (currentMessage.content || '') + nextSlice;
          return {
            ...currentMessage,
            content: updatedContent,
            streamedFinalText: updatedContent,
            agentTimelineSteps: appendProseToAgentTimeline(currentMessage.agentTimelineSteps, nextSlice),
            finalResponseComplete: false,
            finalResponseTraceId: latestChunkMeta.trace_id || currentMessage.finalResponseTraceId || null,
            finalChunkCount: latestChunkMeta.finalChunkCount,
            lastChunkSequence: latestChunkMeta.lastChunkSequence
          } as MessageWithSources;
        }
        return msg;
      })
    }));

    if (pendingChunkBuffer.length) {
      chunkFlushRafId = window.requestAnimationFrame(flushChunkBuffer);
    }
  };

  const scheduleChunkFlush = () => {
    if (chunkFlushRafId !== null) return;
    chunkFlushRafId = window.requestAnimationFrame(flushChunkBuffer);
  };

  const flushRemainingChunks = () => {
    if (chunkFlushRafId !== null) {
      window.cancelAnimationFrame(chunkFlushRafId);
      chunkFlushRafId = null;
    }
    if (!pendingChunkBuffer.length) return;

    const remaining = pendingChunkBuffer;
    pendingChunkBuffer = '';
    setChatState(prevState => ({
      ...prevState,
      messages: prevState.messages.map(msg => {
        if (msg.id === 'agent-streaming') {
          const currentMessage = msg as MessageWithSources;
          const updatedContent = (currentMessage.content || '') + remaining;
          return {
            ...currentMessage,
            content: updatedContent,
            streamedFinalText: updatedContent,
            agentTimelineSteps: appendProseToAgentTimeline(currentMessage.agentTimelineSteps, remaining),
            finalResponseComplete: false,
            finalResponseTraceId: latestChunkMeta.trace_id || currentMessage.finalResponseTraceId || null,
            finalChunkCount: latestChunkMeta.finalChunkCount,
            lastChunkSequence: latestChunkMeta.lastChunkSequence
          } as MessageWithSources;
        }
        return msg;
      })
    }));
  };

  const cancelChunkAnimation = () => {
    if (chunkFlushRafId !== null) {
      window.cancelAnimationFrame(chunkFlushRafId);
      chunkFlushRafId = null;
    }
    pendingChunkBuffer = '';
  };

  const onToken = (token: string) => {
    if (hasReceivedFinalChunk) {
      return;
    }
    setChatState(prevState => ({
      ...prevState,
      messages: prevState.messages.map(msg => {
        if (msg.id === 'agent-streaming') {
          const messageWithSources = msg as MessageWithSources;
          if ((messageWithSources.finalChunkCount || 0) > 0) {
            return msg;
          }
          const nextContent = (msg.content || '') + token;
          return {
            ...msg,
            content: nextContent,
            streamedFinalText: ((msg as MessageWithSources).streamedFinalText || '') + token,
            agentTimelineSteps: appendProseToAgentTimeline(messageWithSources.agentTimelineSteps, token),
          };
        }
        return msg;
      })
    }));
  };

  const onTokenUpdate = (agent: string, thought: string, isFinal: boolean, toolResult?: any) => {
    setChatState(prevState => {
      const hasAgentMessage = prevState.messages.some(msg => msg.id === 'agent-streaming');
      
      if (!hasAgentMessage) {
        const initialTimeline = toolResult
          ? pushToolTimeline([], toolResult)
          : pushThoughtTimeline([], agent, thought, isFinal);
        const agentMessage: MessageWithSources = {
          id: 'agent-streaming',
          content: '',
          isUser: false,
          timestamp: new Date(),
          feedbackGiven: null,
          sources: [],
          changes: '',
          completedThoughts: [{ 
            agent, 
            thought, 
            completion_order: 0,
            ...(toolResult && { toolResult })
          }],
          agentTimelineSteps: initialTimeline,
        };
        
        return {
          ...prevState,
          messages: [...prevState.messages, agentMessage]
        };
      } else {
        return {
          ...prevState,
          messages: prevState.messages.map(msg => {
            if (msg.id === 'agent-streaming') {
              const messageWithSources = msg as MessageWithSources;
              const currentThoughts = messageWithSources.completedThoughts || [];
              
              // AGREGAR pensamiento nuevo (no reemplazar)
              const updatedThoughts = [
                ...currentThoughts, 
                { 
                  agent, 
                  thought, 
                  completion_order: currentThoughts.length,
                  ...(toolResult && { toolResult })
                }
              ];
              const prevTimeline = messageWithSources.agentTimelineSteps || [];
              const nextTimeline = toolResult
                ? pushToolTimeline(prevTimeline, toolResult)
                : pushThoughtTimeline(prevTimeline, agent, thought, isFinal);
              
              return {
                ...messageWithSources,
                completedThoughts: updatedThoughts,
                agentTimelineSteps: nextTimeline,
              } as MessageWithSources;
            }
            return msg;
          })
        };
      }
    });
    
    // Mantener indicador de pensamiento actual
    if (!isFinal) {
      setCurrentStreamingThoughts(prev => {
        const existingIndex = prev.findIndex(t => t.agent === agent);
        
        if (existingIndex >= 0) {
          const newThoughts = [...prev];
          newThoughts[existingIndex] = { agent, thought };
          return newThoughts;
        } else {
          return [...prev, { agent, thought }];
        }
      });
    } else {
      setCurrentStreamingThoughts(prev => 
        prev.filter(t => t.agent !== agent)
      );
    }
  };

  const onFinalResponseChunk = (chunkInfo: {
    chunk: string;
    streamedText: string;
    trace_id?: string;
    sequence?: number;
    timestamp?: string;
    finalChunkCount: number;
    lastChunkSequence: number | null;
  }) => {
    if (!chunkInfo.chunk) return;

    hasReceivedFinalChunk = true;
    latestChunkMeta = {
      trace_id: chunkInfo.trace_id,
      finalChunkCount: chunkInfo.finalChunkCount,
      lastChunkSequence: chunkInfo.lastChunkSequence
    };

    pendingChunkBuffer += chunkInfo.chunk;
    scheduleChunkFlush();
  };

  return { onToken, onTokenUpdate, onFinalResponseChunk, flushRemainingChunks, cancelChunkAnimation };
};

export const useChatState = (workpillId?: string, workpillExecutionId?: string | null) => {
  // 🏪 ESTADO CENTRALIZADO
  const [chatState, setChatState] = useState<ChatState>({
    messages: [],
    isAgentMode: true,
    sidebarSections: [],
    isLoading: false,
    currentInput: ""
  });

  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [streamingMessage, setStreamingMessage] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [showingHistory, setShowingHistory] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<ConversationWithId[]>([]);
  const [currentStreamingThoughts, setCurrentStreamingThoughts] = useState<{ agent: string; thought: string }[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastSendOptionsRef = useRef({
    enableWebSearch: false,
    enableEuipo: false,
    enableEuipoTmclass: false,
    enableEurlex: false,
    enableCendoj: false,
    enableAepd: false,
    enableOepm: false,
    enableBoe: false,
    enableTc: false,
    enableCnmc: false,
  });

  // 🔄 CARGAR CONVERSACIÓN (CENTRALIZADO)
  const loadConversation = useCallback(async (conversationId: string) => {
    // 🛡️ Evitar cargas duplicadas
    if (currentConversationId === conversationId) {
      console.log('🔄 Conversation already loaded:', conversationId);
      return;
    }
    
    // 🛑 CANCELAR STREAMING ACTIVO antes de cambiar de conversación
    if (abortControllerRef.current) {
      console.log('🛑 Cancelling active streaming before switching conversation');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setCurrentStreamingThoughts([]);
    
    try {
      console.log('🔄 Loading conversation:', conversationId);
      setChatState(prev => ({ ...prev, isLoading: true, messages: [] })); // Limpiar mensajes inmediatamente
      
      const messages = await getConversationMessages(conversationId);
      const processedMessages = messages.map(processMessage);
      
      // 🛡️ Verificar que aún queremos esta conversación
      setCurrentConversationId(conversationId);
      setChatState(prev => ({
        ...prev,
        messages: processedMessages,
        isLoading: false
      }));
      
      setShowingHistory(false); // 🔄 Cerrar historial automáticamente
      console.log('✅ Conversation loaded:', processedMessages.length, 'messages');
      
    } catch (error) {
      console.error('❌ Error loading conversation:', error);
      setChatState(prev => ({ ...prev, isLoading: false }));
    }
  }, [currentConversationId]);

  const refreshConversationMessages = useCallback(async (conversationId: string) => {
    try {
      const raw = await getConversationMessages(conversationId);
      const processedMessages = raw.map(processMessage);
      setCurrentConversationId(conversationId);
      setChatState((prev) => ({
        ...prev,
        messages: processedMessages,
        isLoading: false
      }));
    } catch (error) {
      console.error('❌ Error refreshing conversation:', error);
      setChatState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  // 🚀 INICIALIZACIÓN (SOLO UNA VEZ)
  useEffect(() => {
    let isMounted = true;
    
    const initializeChat = async () => {
      if (!isMounted) return;
      
      try {
        setChatState(prev => ({ ...prev, isLoading: true }));
        
        const existingConversations = await getUserConversations(workpillExecutionId);
        
        if (!isMounted) return;
        
        if (existingConversations && existingConversations.length > 0) {
          const conversation = existingConversations[0];
          await loadConversation(conversation.id);
        } else {
          const conversation = await createConversation(workpillId, workpillExecutionId);
          if (isMounted) {
            setCurrentConversationId(conversation.id);
            setChatState(prev => ({ ...prev, messages: [], isLoading: false }));
          }
        }
        
      } catch (error) {
        console.error('❌ Error initializing chat:', error);
        if (isMounted) {
          setChatState(prev => ({ ...prev, isLoading: false, messages: [] }));
        }
      }
    };

    initializeChat();
    
    return () => {
      isMounted = false;
    };
  }, [workpillId, workpillExecutionId]); // 🛡️ Eliminar loadConversation de deps

  // 📤 ENVIAR MENSAJE (SIMPLIFICADO)
  const sendMessage = useCallback(async (
    content: string, 
    attachedDocuments: { id: string; name: string; type: string; isCategory?: boolean }[] = [],
    sendOptions?: {
      enableWebSearch?: boolean;
      enableEuipo?: boolean;
      enableEuipoTmclass?: boolean;
      enableEurlex?: boolean;
      enableCendoj?: boolean;
      enableAepd?: boolean;
      enableOepm?: boolean;
      enableBoe?: boolean;
      enableTc?: boolean;
      enableCnmc?: boolean;
    }
  ) => {
    if (!currentConversationId || !content.trim()) return;
    let flushRemainingChunks: () => void = () => {};
    let cancelChunkAnimation: () => void = () => {};

    // Crear mensaje de usuario
    const userMessage: MessageWithSources = {
      id: `user-${Date.now()}`,
      content,
      isUser: true,
      timestamp: new Date(),
      sources: [],
      // Guardar documentos adjuntos para poder restaurarlos si se cancela
      attachments: attachedDocuments.map(doc => ({
        id: doc.id,
        name: doc.name,
        type: doc.type as any, // Preservar el tipo original (p.ej. 'knowledge_base')
        isCategory: doc.isCategory
      })) as any
    };

    // Crear mensaje temporal del bot inmediatamente (antes de recibir respuesta)
    const agentMessage: MessageWithSources = {
      id: 'agent-streaming',
      content: '',
      isUser: false,
      timestamp: new Date(),
      feedbackGiven: null,
      sources: [],
      changes: '',
      completedThoughts: [],
      streamedFinalText: '',
      finalResponseComplete: false,
      finalResponseTraceId: null,
      finalChunkCount: 0,
      lastChunkSequence: null,
      agentTimelineSteps: [],
    };

    // Agregar mensajes inmediatamente a la UI (usuario + bot temporal)
    setChatState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage, agentMessage],
      isLoading: true
    }));

    let savedUserMessageId: string | null = null;
    let dbReconciledViaPoll = false;
    let reconcilePollTimer: number | null = null;
    let redlineSince = new Date();

    try {
      // Guardar mensaje de usuario
      const documentIds = attachedDocuments.map(d => d.id);
      const savedMessage = await addMessage(
        currentConversationId,
        content,
        true,
        documentIds,
        workpillId,
        false,
        workpillExecutionId
      );
      savedUserMessageId =
        savedMessage?.id != null ? String(savedMessage.id) : null;

      // 🔄 Actualizar el ID temporal del mensaje de usuario con el ID real de la BD
      if (savedMessage && savedMessage.id) {
        setChatState(prev => ({
          ...prev,
          messages: prev.messages.map(msg => 
            msg.id === userMessage.id 
              ? { ...msg, id: savedMessage.id } 
              : msg
          )
        }));
      }

      redlineSince =
        savedMessage?.sent_at != null
          ? new Date(String(savedMessage.sent_at))
          : new Date();
      if (Number.isNaN(redlineSince.getTime())) {
        redlineSince = new Date();
      }

      // Preparar streaming
      setIsStreaming(true);
      setStreamingMessage("");
      abortControllerRef.current = new AbortController();

      // 🔧 Crear callbacks comunes (evita duplicación)
      const streamingCallbacks = createStreamingCallbacks(setChatState, setCurrentStreamingThoughts);
      const { onToken, onTokenUpdate, onFinalResponseChunk } = streamingCallbacks;
      flushRemainingChunks = streamingCallbacks.flushRemainingChunks;
      cancelChunkAnimation = streamingCallbacks.cancelChunkAnimation;

      const finalizeAfterStream = async () => {
        flushRemainingChunks();
        cancelChunkAnimation();
        setIsStreaming(false);
        setStreamingMessage('');
        setCurrentStreamingThoughts([]);
        setChatState((prev) => ({ ...prev, isLoading: false }));
        await new Promise((r) => setTimeout(r, 400));
        await refreshConversationMessages(currentConversationId);
        await loadRedlineDocumentIfPresent(currentConversationId, redlineSince);
      };

      if (savedUserMessageId && currentConversationId) {
        reconcilePollTimer = window.setInterval(() => {
          void (async () => {
            if (dbReconciledViaPoll) return;
            try {
              const rows = await getConversationMessages(currentConversationId);
              const idx = rows.findIndex(
                (r: { id: string }) => String(r.id) === savedUserMessageId
              );
              if (idx === -1) return;
              const assistant = rows
                .slice(idx + 1)
                .find(
                  (r: { is_user?: boolean; content?: string | null }) =>
                    !r.is_user && String(r.content ?? '').trim().length > 0
                );
              if (!assistant) return;
              dbReconciledViaPoll = true;
              abortControllerRef.current?.abort();
            } catch (e) {
              console.warn('[useChatState] reconcile poll failed', e);
            }
          })();
        }, 2500);
      }

      const enableWebSearch = sendOptions?.enableWebSearch ?? false;
      const enableEuipo = sendOptions?.enableEuipo ?? false;
      const enableEuipoTmclass = sendOptions?.enableEuipoTmclass ?? false;
      const enableEurlex = sendOptions?.enableEurlex ?? false;
      const enableCendoj = sendOptions?.enableCendoj ?? false;
      const enableAepd = sendOptions?.enableAepd ?? false;
      const enableOepm = sendOptions?.enableOepm ?? false;
      const enableBoe = sendOptions?.enableBoe ?? false;
      const enableTc = sendOptions?.enableTc ?? false;
      const enableCnmc = sendOptions?.enableCnmc ?? false;
      lastSendOptionsRef.current = {
        enableWebSearch,
        enableEuipo,
        enableEuipoTmclass,
        enableEurlex,
        enableCendoj,
        enableAepd,
        enableOepm,
        enableBoe,
        enableTc,
        enableCnmc,
      };

      try {
        if (chatState.isAgentMode) {
          console.log('[useChatState] AGENT MODE - Saving document...');
          const cfg = configService.getConfig();
          let etId = cfg.activeExecutionTemplateId;
          if (!etId && workpillExecutionId) {
            etId = await TemplateService.getPrimaryDocxExecutionTemplateId(workpillExecutionId);
            if (etId) configService.setActiveExecutionTemplateId(etId);
          }
          try {
            const saveResult = await TemplateService.saveCurrentDocumentTemplate(etId);
            if (saveResult.success) {
              console.log('[useChatState] Document saved:', saveResult.filePath);
            } else {
              console.error('[useChatState] Error saving document:', saveResult.error);
            }
          } catch (error) {
            console.warn('[useChatState] Error saving template:', error);
          }
        }

        await runMultiagentChatStream(currentConversationId, content, documentIds, {
          signal: abortControllerRef.current?.signal,
          isAgentMode: chatState.isAgentMode,
          enableWebSearch,
          enableEuipo,
          enableEuipoTmclass,
          enableEurlex,
          enableCendoj,
          enableAepd,
          enableOepm,
          enableBoe,
          enableTc,
          enableCnmc,
          workpillExecutionId,
          onToken,
          onTokenUpdate,
          onFinalResponseChunk
        });

        await finalizeAfterStream();
      } finally {
        if (reconcilePollTimer !== null) {
          clearInterval(reconcilePollTimer);
        }
      }

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        cancelChunkAnimation();
        if (dbReconciledViaPoll) {
          try {
            flushRemainingChunks();
            cancelChunkAnimation();
            setIsStreaming(false);
            setStreamingMessage('');
            setCurrentStreamingThoughts([]);
            setChatState((prev) => ({ ...prev, isLoading: false }));
            await new Promise((r) => setTimeout(r, 400));
            await refreshConversationMessages(currentConversationId);
            await loadRedlineDocumentIfPresent(currentConversationId, redlineSince);
          } catch (e) {
            console.warn('[useChatState] finalize after db reconcile failed', e);
          }
          return;
        }
        console.log('[useChatState] Streaming aborted - no additional action required');
        return;
      }

      if (
        error instanceof Error &&
        (error as Error & { code?: string }).code === 'STREAM_IDLE_TIMEOUT' &&
        savedUserMessageId &&
        currentConversationId
      ) {
        cancelChunkAnimation();
        try {
          const rows = await getConversationMessages(currentConversationId);
          const idx = rows.findIndex(
            (r: { id: string }) => String(r.id) === savedUserMessageId
          );
          if (idx !== -1) {
            const assistant = rows
              .slice(idx + 1)
              .find(
                (r: { is_user?: boolean; content?: string | null }) =>
                  !r.is_user && String(r.content ?? '').trim().length > 0
              );
            if (assistant) {
              flushRemainingChunks();
              cancelChunkAnimation();
              setIsStreaming(false);
              setStreamingMessage('');
              setCurrentStreamingThoughts([]);
              setChatState((prev) => ({ ...prev, isLoading: false }));
              await new Promise((r) => setTimeout(r, 400));
              await refreshConversationMessages(currentConversationId);
              await loadRedlineDocumentIfPresent(currentConversationId, redlineSince);
              return;
            }
          }
        } catch (e) {
          console.warn('[useChatState] idle timeout reconcile failed', e);
        }
      }

      cancelChunkAnimation();
      console.error('[useChatState] Error sending message:', error);
      setChatState(prev => ({ ...prev, isLoading: false }));
      setIsStreaming(false);
      setStreamingMessage("");
      setCurrentStreamingThoughts([]);
    }
  }, [currentConversationId, chatState.isAgentMode, workpillId, workpillExecutionId, refreshConversationMessages]);

  // 👍 FEEDBACK
  const handleFeedback = useCallback(async (messageId: string, feedback: 'like' | 'dislike') => {
    try {
      await updateMessageFeedback(messageId, feedback === 'like');
      setChatState(prev => ({
        ...prev,
        messages: prev.messages.map(msg => 
          msg.id === messageId ? { ...msg, feedbackGiven: feedback } : msg
        )
      }));
    } catch (error) {
      console.error('❌ Error updating feedback:', error);
    }
  }, []);

  // 🔄 Actualizar estado de changes
  const updateChangesStatus = useCallback(async (messageId: string, status: 'applied' | 'cancelled' | 'accepted' | 'rejected') => {
    console.log('🔄 [useChatState] updateChangesStatus llamado:', { messageId, status });

    if (!messageId) {
      console.error('❌ [useChatState] updateChangesStatus llamado con messageId undefined:', { status });
      return;
    }

    try {
      // Actualizar el estado local inmediatamente (optimistic update)
      setChatState(prev => {
        const messageExists = prev.messages.some(msg => msg.id === messageId);
        const messageBefore = prev.messages.find(msg => msg.id === messageId);
        console.log('🔄 [useChatState] Actualizando estado local:', {
          messageId,
          status,
          messageExists,
          totalMessages: prev.messages.length,
          messageBefore: messageBefore ? { 
            changesStatus: messageBefore.changesStatus, 
            id: messageBefore.id,
            hasChanges: !!messageBefore.changes
          } : null,
          messageIds: prev.messages.map(m => ({ 
            id: m.id, 
            changesStatus: m.changesStatus,
            hasChanges: !!m.changes
          }))
        });

        const updatedMessages = prev.messages.map(msg =>
          msg.id === messageId
            ? { ...msg, changesStatus: status, changesUpdatedAt: new Date() }
            : msg
        );

        const messageAfter = updatedMessages.find(msg => msg.id === messageId);
        console.log('🔄 [useChatState] Estado local actualizado:', {
          messageId,
          messageAfter: messageAfter ? { 
            changesStatus: messageAfter.changesStatus, 
            id: messageAfter.id,
            hasChanges: !!messageAfter.changes
          } : null,
          allMessagesWithChanges: updatedMessages
            .filter(m => m.changes)
            .map(m => ({ id: m.id, changesStatus: m.changesStatus }))
        });

        return {
          ...prev,
          messages: updatedMessages
        };
      });

      // Actualizar en el backend
      console.log('🔄 [useChatState] Actualizando en BD...');
      await updateChangesApplicationStatus(messageId, status);
      console.log('✅ [useChatState] Estado actualizado en BD:', { messageId, status });

    } catch (error) {
      console.error('❌ [useChatState] Error actualizando estado de changes:', error);
      console.error('❌ [useChatState] Error details:', {
        messageId,
        status,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined
      });

      // Revertir el estado local en caso de error
      setChatState(prev => ({
        ...prev,
        messages: prev.messages.map(msg =>
          msg.id === messageId
            ? { ...msg, changesStatus: null, changesUpdatedAt: null }
            : msg
        )
      }));
    }
  }, []);

  // Note: cleanupDiffFromDocument removed - no longer needed with OOXML track changes
  // Word handles track changes natively, no manual cleanup required

  // 🔄 TOGGLE MODO AGENTE
  const toggleAgentMode = useCallback(() => {
    setChatState(prev => ({ ...prev, isAgentMode: !prev.isAgentMode }));
  }, []);

  // 🆕 NUEVO CHAT
  const startNewChat = useCallback(async () => {
    try {
      const conversation = await createConversation(workpillId, workpillExecutionId);
      setCurrentConversationId(conversation.id);
      setChatState(prev => ({ ...prev, messages: [], isLoading: false }));
      setShowingHistory(false);
    } catch (error) {
      console.error('❌ Error creating new chat:', error);
    }
  }, [workpillId, workpillExecutionId]);

  // 📜 HISTORIAL
  const showHistory = useCallback(async () => {
    if (showingHistory) {
      setShowingHistory(false);
      return;
    }
    
    try {
      console.log('📜 Loading history...');
      const conversations = await getUserConversations(workpillExecutionId);
      setConversationHistory(conversations || []);
      setShowingHistory(true);
      console.log('📜 History loaded:', conversations?.length || 0, 'conversations');
    } catch (error) {
      console.error('❌ Error loading history:', error);
      setShowingHistory(false);
    }
  }, [workpillExecutionId, showingHistory]);

  const hideHistory = useCallback(() => {
    setShowingHistory(false);
  }, []);

  // 🗑️ BORRAR CHAT
  const deleteChat = useCallback(async (conversationId: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    
    try {
      console.log('🗑️ Deleting conversation:', conversationId);
      
      // 1. Optimistic update in history list
      setConversationHistory(prev => prev.filter(c => c.id !== conversationId));
      
      // 2. If it's the current conversation, clear it
      if (currentConversationId === conversationId) {
        setChatState(prev => ({ ...prev, messages: [] }));
        setCurrentConversationId(null);
      }
      
      // 3. Call API
      await deleteConversation(conversationId);
      console.log('✅ Conversation deleted:', conversationId);
      
    } catch (error) {
      console.error('❌ Error deleting conversation:', error);
      // Reload history to restore state in case of error
      showHistory();
    }
  }, [currentConversationId, showHistory]);

  // 🛑 DETENER GENERACIÓN
  const stopGeneration = useCallback(() => {
    console.log('🛑 Stopping generation by user');
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setCurrentStreamingThoughts([]);
    
    // 1. Encontrar el mensaje del usuario a restaurar usando el estado actual
    const messages = chatState.messages;
    const streamingMsgIndex = messages.findIndex(m => m.id === 'agent-streaming');
    
    let lastUserMessage: MessageWithSources | null = null;
    let userMessageIndex = -1; // Índice en el array actual
    
    if (streamingMsgIndex > 0) {
      // Buscar el mensaje del usuario justo antes del mensaje de streaming
      for (let i = streamingMsgIndex - 1; i >= 0; i--) {
        if (messages[i].isUser) {
          lastUserMessage = messages[i] as MessageWithSources;
          userMessageIndex = i;
          break;
        }
      }
    }
    
    // 2. Actualizar el estado UI
    setChatState(prev => {
      // Recalculamos índices sobre el estado 'prev' para seguridad
      const currentStreamingMsgIndex = prev.messages.findIndex(m => m.id === 'agent-streaming');
      let currentUserMessageIndex = -1;
      
      if (currentStreamingMsgIndex > 0) {
         for (let i = currentStreamingMsgIndex - 1; i >= 0; i--) {
            if (prev.messages[i].isUser) {
              currentUserMessageIndex = i;
              break;
            }
         }
      }

      const streamingMsg = prev.messages.find(m => m.id === 'agent-streaming');
      
      // Si el mensaje del agente tiene contenido, mantenerlo como mensaje parcial
      // PERO también eliminar el mensaje del usuario para restaurarlo en el input
      if (streamingMsg && (streamingMsg.content || '').trim()) {
        return {
          ...prev,
          isLoading: false,
          messages: prev.messages
            .filter((_, index) => index !== currentUserMessageIndex) // Eliminar mensaje del usuario
            .map(msg => 
              msg.id === 'agent-streaming' 
                ? { ...msg, id: `interrupted-${Date.now()}`, content: msg.content + '\n\n*[Generation interrupted]*' }
                : msg
            )
        };
      }
      
      // Si no tiene contenido, eliminar tanto el mensaje del agente como el del usuario
      if (currentUserMessageIndex >= 0) {
        return {
          ...prev,
          isLoading: false,
          messages: prev.messages.filter((_, index) => 
            (prev.messages[index].id !== 'agent-streaming') && index !== currentUserMessageIndex
          )
        };
      }
      
      // Si no hay mensaje del usuario, solo eliminar el del agente
      return {
        ...prev,
        isLoading: false,
        messages: prev.messages.filter(msg => msg.id !== 'agent-streaming')
      };
    });
    
    // 3. Devolver el contenido del mensaje del usuario encontrado en el paso 1
    if (lastUserMessage) {
      // 🗑️ Si el mensaje ya se guardó en BD (tiene ID real), eliminarlo
      if (!lastUserMessage.id.startsWith('user-')) {
        console.log('🗑️ Deleting cancelled message from DB:', lastUserMessage.id);
        // Importar deleteMessage dinámicamente para evitar ciclos o usar la referencia existente si está importada
        import('../services/conversationService').then(({ deleteMessage }) => {
          deleteMessage((lastUserMessage as any).id).catch(err => {
            console.warn('⚠️ Error deleting cancelled message from DB:', err);
          });
        });
      }

      const attachments = (lastUserMessage as MessageWithSources).attachments || [];
      return {
        content: (lastUserMessage as MessageWithSources).content,
        attachments: attachments.map((att: any) => ({
          id: att.id,
          name: att.name,
          type: att.type,
          isCategory: att.isCategory
        }))
      };
    }
    return null;
  }, [chatState.messages]);

  // 🔄 REEJECUTAR MENSAJE DEL AGENTE
  const retryAgentMessage = useCallback(async (botMessageId: string) => {
    if (!currentConversationId) {
      console.error('No active conversation');
      return;
    }
    
    // Encontrar el mensaje del bot y el mensaje de usuario anterior
    const botMessageIndex = chatState.messages.findIndex(m => m.id === botMessageId);
    if (botMessageIndex === -1) {
      console.error('Bot message not found');
      return;
    }
    
    // Buscar el mensaje de usuario anterior (debe estar justo antes del bot)
    const userMessageIndex = botMessageIndex - 1;
    if (userMessageIndex < 0 || !chatState.messages[userMessageIndex].isUser) {
      console.error('No previous user message found');
      return;
    }
    
    const userMessage = chatState.messages[userMessageIndex];
    const botMessage = chatState.messages[botMessageIndex];
    
    // Extraer documentos adjuntos del mensaje de usuario si los tiene
    const attachedDocuments = userMessage.attachments || [];
    
    try {
      // 1. Eliminar mensajes del estado local primero (mejor UX)
      setChatState(prev => ({
        ...prev,
        messages: prev.messages.filter((_, index) => 
          index !== userMessageIndex && index !== botMessageIndex
        )
      }));
      
      // 2. Eliminar mensajes de la BD
      // Necesitamos los IDs reales de BD, no los temporales
      // Si el mensaje tiene un ID que empieza con 'user-' o 'agent-streaming', 
      // significa que es temporal y no está en BD aún
      if (!botMessage.id.startsWith('agent-streaming') && !botMessage.id.startsWith('interrupted-')) {
        try {
          await deleteMessage(botMessage.id);
        } catch (error) {
          console.warn('Error deleting bot message from DB:', error);
        }
      }
      
      if (!userMessage.id.startsWith('user-')) {
        try {
          await deleteMessage(userMessage.id);
        } catch (error) {
          console.warn('Error deleting user message from DB:', error);
        }
      }
      
      // 3. Reenviar el mensaje (preserve web search + connector flags)
      const att = (attachedDocuments || []).map((att: any) => ({
        id: att.id,
        name: att.name,
        type: att.type,
        isCategory: att.isCategory,
      }));
      await sendMessage(userMessage.content, att, lastSendOptionsRef.current);
      
    } catch (error) {
      console.error('Error retrying message:', error);
      // Recargar la conversación para restaurar el estado
      try {
        await loadConversation(currentConversationId);
      } catch (reloadError) {
        console.error('Error reloading conversation:', reloadError);
      }
    }
  }, [currentConversationId, chatState.messages, sendMessage, loadConversation]);

  // 🧹 CLEANUP
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    messages: chatState.messages,
    isAgentMode: chatState.isAgentMode,
    sidebarSections: chatState.sidebarSections,
    isLoading: chatState.isLoading,
    sendMessage,
    stopGeneration,
    handleFeedback,
    updateChangesStatus,
    toggleAgentMode,
    startNewChat,
    showHistory,
    hideHistory,
    deleteChat,
    loadConversation,
    isStreaming,
    streamingMessage,
    currentConversationId,
    showingHistory,
    conversationHistory,
    currentStreamingThoughts,
    retryAgentMessage
  };
};

